import {
  Banknote,
  BookOpenCheck,
  Building2,
  CalendarDays,
  CalendarRange,
  CalendarSync,
  ClipboardCheck,
  GraduationCap,
  HandCoins,
  KeyRound,
  LayoutDashboard,
  type LucideIcon,
  Megaphone,
  NotebookPen,
  ReceiptText,
  Settings,
  ToggleRight,
  Users,
  UserSquare2,
  Vault,
  Wallet,
} from 'lucide-react';
import type { MessageKey } from '@/i18n';
import type { FeatureKey } from '@/lib/api/types';
import type { Permission } from '@/lib/auth/permissions';

export type BadgeSource = 'payments' | 'fees' | 'advances' | 'payroll';

export interface NavItem {
  to: string;
  label: MessageKey;
  icon: LucideIcon;
  feature?: FeatureKey;
  perm?: Permission;
  badge?: BadgeSource;
  /** Active only on this exact path. */
  end?: boolean;
  /** Not active under this sub-path (it has its own item). */
  exclude?: string;
}

export function isNavActive(item: NavItem, pathname: string): boolean {
  if (item.end) return pathname === item.to;
  if (item.exclude && pathname.startsWith(item.exclude)) return false;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export interface NavGroup {
  label: MessageKey;
  items: NavItem[];
}

export const SCHOOL_NAV: NavGroup[] = [
  {
    label: 'nav.group.general',
    items: [
      { to: '/', label: 'nav.dashboard', icon: LayoutDashboard, end: true, perm: 'office' },
      { to: '/students', label: 'nav.students', icon: Users, perm: 'office' },
      { to: '/notes', label: 'nav.notes', icon: NotebookPen, feature: 'teacherNotes', perm: 'approve' },
      { to: '/announcements', label: 'nav.announcements', icon: Megaphone, feature: 'announcements', perm: 'attendance' },
    ],
  },
  {
    label: 'nav.group.attendance',
    items: [
      { to: '/attendance', label: 'nav.attendance', icon: ClipboardCheck, feature: 'studentAttendance', perm: 'attendance', exclude: '/attendance/report' },
      { to: '/attendance/report', label: 'nav.attendanceReport', icon: CalendarRange, feature: 'studentAttendance', perm: 'office' },
    ],
  },
  {
    label: 'nav.group.academics',
    items: [
      { to: '/timetable', label: 'nav.timetable', icon: CalendarDays, feature: 'timetable', perm: 'attendance' },
      { to: '/grades', label: 'nav.grades', icon: GraduationCap, feature: 'examsGrades', perm: 'attendance' },
    ],
  },
  {
    label: 'nav.group.finance',
    items: [
      { to: '/receive', label: 'nav.receive', icon: HandCoins, feature: 'fees', perm: 'write' },
      { to: '/payments', label: 'nav.payments', icon: ReceiptText, feature: 'fees', perm: 'office', badge: 'payments' },
      { to: '/cash', label: 'nav.cash', icon: Vault, perm: 'office' },
      { to: '/fees', label: 'nav.fees', icon: BookOpenCheck, feature: 'fees', perm: 'office', badge: 'fees' },
    ],
  },
  {
    label: 'nav.group.staff',
    items: [
      { to: '/staff', label: 'nav.staff', icon: UserSquare2, feature: 'payroll', perm: 'office' },
      { to: '/payroll', label: 'nav.payroll', icon: Banknote, feature: 'payroll', perm: 'office', badge: 'payroll' },
      { to: '/advances', label: 'nav.advances', icon: Wallet, feature: 'advances', perm: 'office', badge: 'advances' },
    ],
  },
  {
    label: 'nav.group.admin',
    items: [
      { to: '/new-year', label: 'nav.newYear', icon: CalendarSync, perm: 'approve' },
      { to: '/settings', label: 'nav.settings', icon: Settings, perm: 'approve', exclude: '/settings/features' },
      { to: '/settings/features', label: 'nav.features', icon: ToggleRight, perm: 'approve' },
    ],
  },
];

export const PLATFORM_NAV: NavGroup[] = [
  {
    label: 'nav.group.platform',
    items: [
      { to: '/platform', label: 'nav.platformOverview', icon: LayoutDashboard, end: true },
      { to: '/platform/schools', label: 'nav.platform', icon: Building2 },
      { to: '/platform/people', label: 'nav.platformPeople', icon: Users },
      { to: '/platform/codes', label: 'nav.platformCodes', icon: KeyRound },
    ],
  },
];
